import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { useQuery } from '@tanstack/react-query';
import { POLLING_RESPONSE, PROFILE, PollingResponse, fetchPollingHistory } from './pollingApi';
import { useParams } from 'react-router';
import { parseISO } from 'date-fns';
import { GraphsBox } from './GraphsBox';
import { GraphLegend } from './GraphLegend';
import { PollingChartAdditionalMenu } from './PollingChartAdditionalMenu';
import { PoolingChartEntryData, PoolingGraph } from 'common/components/Graphs/PoolingGraph';

import { useDebounce } from 'src/common/useDebounce';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { useTranslation } from 'react-i18next';
import { useGraphSessionRange } from 'common/components/Graphs/Sessions/hooks/useGraphSessionRange';
import { SessionHistoryContext } from './SessionHistoryContext';
import { SimInventoryCan } from 'permissions/PermissionProvider';
import { Actions, Subjects } from 'permissions/ability';
import { palette } from 'theme/palette';
import { pxToRem } from 'src/common/utils/fonts';
import { useHasFeatureFlag } from 'featureFlags/useHasFeatureFlag.ts';

const SINGLE_FETCH_PAGE_SIZE = 100000;

const POLLING_RESPONSE_COLORS: Record<PollingResponse, string> = {
  [POLLING_RESPONSE.ACK]: palette.blue['600'],
  [POLLING_RESPONSE.NACK]: palette.amber['600'],
  [POLLING_RESPONSE.NO_RESPONSE]: palette.blue['600'],
} as const;

export const PollingChart = React.memo(() => {
  const IPAeStatusEnabled = useHasFeatureFlag('IPAeStatus');
  const { visibleRange, selectVisibleRange, validRange, selectRange } = useGraphSessionRange();
  const { selectSession } = useContext(SessionHistoryContext);

  const { euicc } = useParams();

  const debouncedFrom = useDebounce(visibleRange.from, 500);
  const debouncedTo = useDebounce(visibleRange.to, 500);

  const isValidRange =
    visibleRange.from instanceof Date &&
    !Number.isNaN(visibleRange.from.getTime()) &&
    visibleRange.to instanceof Date &&
    !Number.isNaN(visibleRange.to.getTime());

  const { data, isFetching } = useQuery({
    queryKey: ['SessionHistory', 'PollingData', euicc!, debouncedFrom, debouncedTo],
    queryFn: async ({ signal }) => {
      const data = await fetchPollingHistory(
        euicc!,
        new Date(visibleRange.from),
        new Date(visibleRange.to),
        0,
        SINGLE_FETCH_PAGE_SIZE,
        signal,
      );

      const entries = data.items.reduce<PoolingChartEntryData[]>((accumulator, item) => {
        const parsedTimestamp = parseISO(item.timestamp);
        if (Number.isNaN(parsedTimestamp.getTime())) {
          return accumulator;
        }

        let color = POLLING_RESPONSE_COLORS[item.pollingResponse];
        if (item.isPaused && IPAeStatusEnabled) {
          color = palette.teal[400];
        }

        accumulator.push({
          x: parsedTimestamp,
          y: item.profileId === PROFILE.PROFILE_0 ? '0.5' : '-0.5',
          color,
        });

        return accumulator;
      }, []);

      return entries.sort((left, right) => left.x.getTime() - right.x.getTime());
    },
    enabled: isValidRange,
    throwOnError: true,
  });

  const [deferredPooling, setDeferredPooling] = useState<PoolingChartEntryData[]>([]);

  useEffect(() => {
    setDeferredPooling(data ?? []);
  }, [data]);

  const { t } = useTranslation();

  const tooltipData = useMemo(() => {
    const legends = [
      {
        color: POLLING_RESPONSE_COLORS[0],
        description: t('rSimInventory.pollingChartAcknowledged'),
      },
      {
        color: POLLING_RESPONSE_COLORS[1],
        description: t('rSimInventory.pollingChartNotAcknowledged'),
      },
    ];

    if (IPAeStatusEnabled) {
      legends.push({
        color: palette.teal[400],
        description: t('rSimInventory.paused'),
      });
    }
    return legends;
  }, [IPAeStatusEnabled, t]);

  const onZoomComplete = useCallback(
    (range: [Date, Date]) => {
      if (selectSession) {
        selectSession({ positionX: null, index: null, setName: null });
      }

      selectVisibleRange(range[0], range[1]);
    },
    [selectVisibleRange],
  );

  return (
    <GraphsBox
      rightOffsetPx={40}
      title={t('rSimInventory.pollingChartTitle')}
      legend={<GraphLegend topLabel="Primary" bottomLabel="Backup" axisHeight={35} />}
      colorLegendItems={tooltipData}
    >
      <PoolingGraph
        min={validRange.from.getTime()}
        max={validRange.to.getTime()}
        from={visibleRange.from.getTime()}
        to={visibleRange.to.getTime()}
        entries={deferredPooling}
        onZoomComplete={onZoomComplete}
        onPan={selectRange}
      />
      {isFetching && (
        <Box
          sx={{
            position: 'absolute',
            top: `calc(50% - ${pxToRem(14)})`,
            left: '50%',
            transform: 'translateX(-50%) translateY(-50%)',
          }}
        >
          <CircularProgress />
        </Box>
      )}
      <SimInventoryCan I={Actions.export} a={Subjects.poolingLogs}>
        <Box sx={{ position: 'absolute', top: pxToRem(-8), right: pxToRem(4) }}>
          <PollingChartAdditionalMenu
            euicc={euicc!}
            from={visibleRange.from}
            to={visibleRange.to}
          />
        </Box>
      </SimInventoryCan>
    </GraphsBox>
  );
});
